export enum AiProvider {
    OPENAI = 0,
    ANTHROPIC = 1,
    GOOGLE = 2,
    DEEPSEEK = 3,
    MISTRAL = 4,
    KIMI = 5,
    MIMO = 6,
}

/**
 * Whose key the agent runs on. OWN_KEY — the owner's provider key, no credits spent (usage is only
 * shown). PLATFORM — our key: no `api_key` needed, requests are billed in the owner's credits at the
 * model's price (`get_ai_models`). Both need an active Premium.
 */
export enum AgentKeySource {
    OWN_KEY = 0,
    PLATFORM = 1,
}

export interface AgentCredential {
    bot_id: string
    provider: AiProvider
    model_name: string
    organization_id?: string
    is_active: boolean
    created_at?: string
    updated_at?: string
    system_prompt_template?: string
    key_source: AgentKeySource
}

export interface CreateAgentCredentialRequest {
    bot_id: string
    provider: AiProvider
    model_name: string
    api_key: string
    organization_id?: string
    system_prompt_template?: string
    /** Defaults to OWN_KEY; with PLATFORM `api_key` is ignored (send ''). */
    key_source?: AgentKeySource
}

export interface UpdateAgentCredentialRequest {
    bot_id: string
    provider: AiProvider
    model_name: string
    api_key: string
    organization_id?: string
    system_prompt_template?: string
    /** Defaults to OWN_KEY; with PLATFORM `api_key` is ignored (send ''). */
    key_source?: AgentKeySource
}

export interface DeleteAgentCredentialRequest {
    bot_id: string
    provider: AiProvider
}
// ── MCP servers ──────────────────────────────────────────────────────────────

/** Pseudo server id of the built-in (system) MCP server in the MCP server API. */
export const SYSTEM_MCP_SERVER_ID = 'system'

export enum McpAuthState {
    NOT_REQUIRED = 0,
    SIGN_IN_REQUIRED = 1,
    SIGNED_IN = 2,
}

export enum McpToolKind {
    READ_ONLY = 1,
    WRITE = 2,
    INTERACTIVE = 3,
}

export enum McpToolPermission {
    ALWAYS_ALLOW = 1,
    NEEDS_APPROVAL = 2,
    BLOCKED = 3,
}

export enum McpAuthFlowState {
    NONE = 0,
    PENDING = 1,
    CONNECTED = 2,
    FAILED = 3,
}

export interface AgentMcpServer {
    id: string
    bot_id: string
    name: string
    url: string
    is_enabled: boolean
    created_at?: string
    updated_at?: string
    catalog_id?: string | null
    auth_state: McpAuthState
    has_oauth_client: boolean
    /** Null for servers added before sign-in modes existed. */
    auth_mode?: McpAuthMode | null
    oauth_client_mode?: McpOAuthClientMode | null
    /** Names of the request headers set for the server (values are never returned). */
    header_names: string[]
    /** From the catalog entry, when there is one. */
    icon: McpCatalogIcon
    summary?: string | null
    brand_color: string
}

export interface AgentMcpServers {
    system_server_enabled: boolean
    system_server_has_tool_intents: boolean
    servers: AgentMcpServer[]
}

export interface AgentMcpTool {
    name: string
    description: string
    title: string
    kind: McpToolKind
    permission: McpToolPermission
}

export interface AgentMcpServerTools {
    connected: boolean
    error?: string | null
    tools: AgentMcpTool[]
    auth_required: boolean
}

/** When to sign in to the service: before first use, only when it answers 401, or never. */
export type McpAuthMode = 'sign_in_now' | 'sign_in_when_needed' | 'none'

/** Which OAuth client the agent presents: Iopta's published one (CIMD), dynamic registration, or the owner's own. */
export type McpOAuthClientMode = 'cimd' | 'dcr' | 'own'

export type McpVerifiedTier = 'official' | 'partner' | 'community'

export type McpHealth = 'ok' | 'connection_issues' | 'request_errors' | 'slow' | 'not_working' | 'not_enough_data'

/** Icon of a catalog entry: sanitized SVG markup (drawn first) or a URL (https or data:); neither — a monogram. */
export interface McpCatalogIcon {
    url?: string | null
    svg?: string | null
}

/** A catalog entry in a list. */
export interface McpCatalogEntry {
    id: string
    /** Empty for the user's own servers. */
    slug: string
    name: string
    summary: string
    icon: McpCatalogIcon
    brand_color: string
    publisher_name: string
    verified_tier?: McpVerifiedTier | null
    /** new, trending, top, beta, added_manually, shows_ui. */
    tags: string[]
    health: McpHealth | string
    requires_auth: boolean
    /** Connected to the agent the catalog was opened for. */
    installed: boolean
    source: 'directory' | 'private' | string
    added_at?: string | null
}

/** The full card of a catalog entry. */
export interface McpCatalogServer extends McpCatalogEntry {
    /** Markdown. */
    description: string
    publisher_url?: string | null
    website_url?: string | null
    documentation_url?: string | null
    privacy_policy_url?: string | null
    support_url?: string | null
    screenshots: string[]
    endpoint: McpCatalogEndpoint
    auth_mode: McpAuthMode
    oauth_client_mode: McpOAuthClientMode
    oauth_client_secret: 'not_needed' | 'optional' | 'required'
    oauth_documentation?: string | null
    data_sent_to?: string | null
    passes_on: boolean
    object_acted_on: string[]
    work_department: string[]
    industry: string[]
    life_area: string[]
    subject: string[]
    tools: string[]
    prompts: string[]
    review_state: 'none' | 'pending' | 'done'
    reviewed_by?: McpVerifiedTier | null
    visibility: 'public' | 'unlisted' | 'private'
}

export type McpEndpointType = 'fixed' | 'one_of' | 'user_provided' | 'user_provided_pattern'

export interface McpCatalogEndpoint {
    type: McpEndpointType
    url?: string | null
    options: { label: string, description: string, url: string }[]
    hosts: string[]
    pattern?: string | null
    documentation?: string | null
}

export type McpCatalogOrder = 'trending' | 'recommended' | 'added_at' | 'name'

export type McpTaxonomyKind = 'object_acted_on' | 'work_department' | 'industry' | 'life_area' | 'subject'

export interface McpCatalogQuery {
    bot_id?: string | null
    order_by: McpCatalogOrder
    query?: string | null
    verified_tiers?: string[]
    tags?: string[]
    object_acted_on?: string[]
    work_department?: string[]
    industry?: string[]
    life_area?: string[]
    subject?: string[]
    ids?: string[]
    slugs?: string[]
    page_size?: number
    page_token?: string | null
}

export interface McpCatalogFacet {
    /** verified_tier, tag or a taxonomy kind. */
    field: string
    values: { value: string, count: number }[]
}

export interface McpCatalogPage {
    entries: McpCatalogEntry[]
    next_page_token?: string | null
    total: number
    facets: McpCatalogFacet[]
}

export interface McpTaxonomyValue {
    kind: McpTaxonomyKind | string
    value: string
    label: string
    position: number
}

export interface McpHeader {
    name: string
    value: string
}

export type McpProbeStepId = 'connect' | 'auth_metadata' | 'auth_server'

export interface McpProbeStep {
    id: McpProbeStepId | string
    status: 'ok' | 'failed' | 'skipped'
    http_status?: number | null
    message: string
}

/** Result of checking a server before adding it (the "own server" wizard). */
export interface McpProbeResult {
    steps: McpProbeStep[]
    /** The server answers and it's clear how to sign in (or no sign-in is needed). */
    succeeded: boolean
    error?: string | null
    name?: string | null
    title?: string | null
    summary?: string | null
    description?: string | null
    website_url?: string | null
    icon: McpCatalogIcon
    tools: string[]
    prompts: string[]
    shows_ui: boolean
    suggested_auth_mode: McpAuthMode
    suggested_oauth_client_mode: McpOAuthClientMode
    supports_dcr: boolean
    supports_cimd: boolean
    authorization_server?: string | null
}

export interface McpAuthStart {
    state: McpAuthFlowState
    authorization_url?: string | null
    error?: string | null
}

export interface McpAuthStatus {
    state: McpAuthFlowState
    error?: string | null
    /** Current sign-in page — differs from the opened one if the service restarted the sign-in. */
    authorization_url?: string | null
}

// ── Tool approvals (AgentToolApprovalsApi) ───────────────────────────────────

/** Owner's decision on a pending tool call. ALLOW_ALWAYS = ALLOW + the tool becomes ALWAYS_ALLOW for this agent. */
export enum AgentToolApprovalDecision {
    UNSPECIFIED = 0,
    ALLOW = 1,
    ALLOW_ALWAYS = 2,
    DENY = 3,
}

export enum AgentToolApprovalStatus {
    UNSPECIFIED = 0,
    PENDING = 1,
    /** Approved, the call is being executed. */
    APPROVED = 2,
    DENIED = 3,
    EXECUTED = 4,
    FAILED = 5,
    EXPIRED = 6,
}

/** What the agent was doing when it asked. */
export enum AgentToolApprovalSource {
    UNSPECIFIED = 0,
    /** A mention/reply in a channel. */
    CHAT = 1,
    SCHEDULED = 2,
    BOARD_TASK = 3,
    /** A request from another agent. */
    AGENT = 4,
}

export interface AgentToolApproval {
    id: string
    bot_id: string
    bot_name: string
    /** Absolute public URL or ''. */
    bot_avatar_url: string
    /** `SYSTEM_MCP_SERVER_ID` for built-in tools, otherwise an external MCP server id. */
    server_id: string
    server_name: string
    tool_name: string
    tool_title: string
    /** Arguments chosen by the model, may be cut to 4000 chars. */
    arguments_json: string
    source: AgentToolApprovalSource
    group_id: string
    channel_id: string
    channel_name: string
    board_card_id: string
    board_card_title: string
    initiator_id: string
    initiator_name: string
    status: AgentToolApprovalStatus
    created_at?: string | null
    expires_at?: string | null
}

export interface AgentToolApprovals {
    approvals: AgentToolApproval[]
}
